import React, { useState } from 'react';
import { Layout } from '../components/Layout';
import { communicationApi } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { MessageSquare, Send, Loader2, CheckCircle2, Crown } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const SMSPage = () => {
  const { admin } = useAuth();
  const isPremium = admin?.role === 'admin' || admin?.account_type === 'premium';
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ phone_number: '', message: '' });
  const [history, setHistory] = useState([]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!isPremium) {
      toast.error('Fonctionnalite reservee aux comptes Premium');
      return;
    }
    setLoading(true);
    try {
      await communicationApi.sendSMS(form);
      toast.success('SMS envoye avec succes');
      setHistory(prev => [{ ...form, timestamp: new Date().toISOString(), status: 'sent' }, ...prev]);
      setForm({ phone_number: '', message: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (d) => { try { return new Date(d).toLocaleString('fr-FR'); } catch { return d; } };

  if (!isPremium) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center py-20 text-center" data-testid="sms-page-locked">
          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mb-4">
            <Crown className="w-8 h-8 text-amber-500" />
          </div>
          <h2 className="text-xl font-bold text-slate-900 mb-2">Fonctionnalite Premium</h2>
          <p className="text-slate-500 max-w-md">L'envoi de SMS est reserve aux comptes Premium. Contactez votre administrateur pour activer cette fonctionnalite.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="sms-page">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="font-heading font-bold text-xl md:text-2xl text-slate-900">SMS</h1>
            <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-700 border border-amber-200">PREMIUM</span>
          </div>
          <p className="text-slate-500 text-sm mt-1">Envoyez des SMS personnalises aux clients</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="border-slate-200 shadow-sm rounded-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <MessageSquare className="w-5 h-5 text-[#0066FF]" />
                Nouveau SMS
              </CardTitle>
              <CardDescription>Envoyez un SMS personnalise a un client</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSend} className="space-y-4">
                <div className="space-y-2">
                  <Label>Numero de telephone *</Label>
                  <Input type="tel" placeholder="+33 6 12 34 56 78" value={form.phone_number} onChange={e => setForm({...form, phone_number: e.target.value})} required className="rounded-lg" data-testid="sms-phone" />
                </div>
                <div className="space-y-2">
                  <Label>Message *</Label>
                  <Textarea placeholder="Redigez votre SMS ici..." value={form.message} onChange={e => setForm({...form, message: e.target.value})} required className="rounded-lg min-h-[200px] resize-y" data-testid="sms-message" />
                  <p className="text-xs text-slate-400">{form.message.length}/160 caracteres</p>
                </div>
                <Button type="submit" className="w-full bg-[#0066FF] hover:bg-[#0052CC] rounded-lg" disabled={loading} data-testid="sms-send-btn">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                  Envoyer le SMS
                </Button>
              </form>
            </CardContent>
          </Card>

          <Card className="border-slate-200 shadow-sm rounded-lg">
            <CardHeader>
              <CardTitle className="text-lg">Historique des envois</CardTitle>
            </CardHeader>
            <CardContent>
              {history.length === 0 ? (
                <div className="text-center py-12 text-slate-400">
                  <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-50" strokeWidth={1} />
                  <p>Aucun SMS envoye</p>
                </div>
              ) : (
                <div className="space-y-3 max-h-[500px] overflow-y-auto">
                  {history.map((item, i) => (
                    <div key={i} className="p-3 bg-slate-50 rounded-lg border border-slate-100">
                      <div className="flex items-center gap-2 mb-1">
                        <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                        <span className="text-xs text-slate-500">{formatDate(item.timestamp)}</span>
                      </div>
                      <p className="text-sm font-medium text-slate-900">{item.phone_number}</p>
                      <p className="text-xs text-slate-500 truncate">{item.message}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default SMSPage;
